import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { PartOfHub, RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

const PATH = "/fractional-cto-malaysia";
const CTA_SOURCE_HERO = "fractional_cto_malaysia_hero";
const CTA_SOURCE_BAND = "fractional_cto_malaysia_footer";

const title = "Fractional CTO in Malaysia | Timothy Ng, Scryla";
const description =
  "Deadlines slipping as technical decisions outgrow the team? Fractional CTO and technical advisor for Malaysian founders and boards, remote-first on MYT (GMT+8).";

const WHEN = [
  {
    title: "You are building for more than one market at once",
    body: "Malaysian products rarely stay Malaysian. Multi-currency, multi-language and cross-border data handling get bolted on late and expensively unless someone makes the call early.",
  },
  {
    title: "Engineering is split across states or borders",
    body: "A team spread between KL, Penang, Johor and an offshore partner needs one person reading committed scope against shipped work, and writing down what is actually true.",
  },
  {
    title: "Data protection obligations are catching up with you",
    body: "The Personal Data Protection Act 2010 (Act 709) and the JPDP's current guidance set expectations your systems have to meet in practice, not on paper. That is a technical review, not a policy document.",
  },
  {
    title: "Regional or international investors are about to look",
    body: "Diligence finds what it finds. Knowing it first, with a sequenced plan attached, beats discovering it in a data room halfway through a raise.",
  },
];

const WORKING = [
  {
    title: "MYT working hours",
    body: "MYT holds GMT+8 with no daylight saving, so the UK gap is seven hours in summer and eight in winter and the overlap window never moves on your side. Personal data work runs against the Personal Data Protection Act 2010 (Act 709) and the JPDP's current guidance, treated as an engineering and governance question rather than legal advice.",
  },
  {
    title: "Remote-first delivery",
    body: "Fixed weekly contact, board attendance by video, and written reporting your team and your investors can both act on, the same standard applied to UK clients.",
  },
  {
    title: "On-site where it counts",
    body: "Kuala Lumpur, Penang, Johor or wherever the team sits: on-site time is available where a board or team genuinely needs it, arranged and costed per engagement.",
  },
];

const FAQ = [
  {
    q: "Do you work with companies based in Malaysia?",
    a: "Yes. Engagements run remote by default, with fixed weekly contact and board attendance by video. On-site time in Malaysia is available where it changes the outcome, arranged and costed per engagement.",
  },
  {
    q: "Are you based in Malaysia?",
    a: `No. I am based in ${SITE.location} and work with Malaysian clients remote-first, on your hours. I would rather state that plainly than present a service area as a local office.`,
  },
  {
    q: "Which parts of Malaysia do you cover?",
    a: "The whole country on the same remote-first basis. Kuala Lumpur and the Klang Valley have their own page because that is where most enquiries come from; Penang, Johor and elsewhere are covered identically.",
  },
  {
    q: "What sectors do you know?",
    a: "Fintech, iGaming and gambling, digital health, SaaS, data and agencies. Those are sectors I have run technology and operations in, not sectors I have read about.",
  },
  {
    q: "How is this different from a non-executive director role?",
    a: "A fractional CTO works inside the business, owning technical decisions and leading the team. A non-executive director sits outside it, holding the executive to account and giving the board independent assurance. I keep the two separate and will say which one you actually need.",
  },
];

export const Route = createFileRoute("/fractional-cto-malaysia")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta("fractional-cto-malaysia", "Fractional CTO for Malaysia, GMT+8 | Timothy Ng"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO for Malaysia | Timothy Ng, Scryla",
          areaServed: AREAS.malaysia,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.malaysia,
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      breadcrumbMeta(PATH),
    ],
  }),
  component: MalaysiaPage,
});

function MalaysiaPage() {
  return (
    <div>
      <section
        aria-labelledby="fctomy-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Fractional CTO · Malaysia</Eyebrow>
          <h1
            id="fctomy-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO for Malaysian scale-ups.
          </h1>
          <Lead className="mt-6">
            Technical decisions outgrowing the team and deadlines slipping? Senior technical
            judgement for founders and boards across Malaysia, without a full-time executive hire.
            Former CTO and COO, and founder of my own consultancy since 2014.
          </Lead>
          <PartOfHub hub="/fractional-cto-southeast-asia" className="mt-5" />
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctomy-when-heading">
        <Container>
          <Eyebrow>When founders call</Eyebrow>
          <h2
            id="fctomy-when-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Four situations I am usually brought into
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {WHEN.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <RelatedLinks className="mt-7" paths={["/fractional-cto-kuala-lumpur"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctomy-work-heading">
        <Container>
          <Eyebrow>How the engagement works</Eyebrow>
          <h2
            id="fctomy-work-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Working with Malaysian clients
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {WORKING.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-[62ch] text-[15px] leading-relaxed text-body">
            No lock-in, and no interest in staying longer than the business needs me. When you are
            ready to bring the role in-house, I help you hire well and step back.
          </p>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctomy-quotes-heading">
        <Container>
          <h2 id="fctomy-quotes-heading" className="sr-only">
            What founders say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.steveKellyShort} />
            <Quote item={TESTIMONIALS.aaronBiggs} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctomy-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fctomy-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Malaysia FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={["/fractional-cto-southeast-asia", "/fractional-cto-vs-full-time-vs-agency"]}
          />
        </Container>
      </section>

      <CtaBand
        heading="Building in Malaysia?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
